import { sumBy } from 'lodash';
import { getStatusColor } from 'components/Status';
import { appConfig } from 'config/app';
import { Appearance } from 'design';
import {
  AlertType,
  DisbursementMethod,
  HoldbackType,
  IBasePayoutState,
  IEmployeePayrollTip,
  IEmployeeSiteWithholdings,
  IHoldbackFixed,
  IHoldbackPercent,
  IPayee,
  IPayeeTotals,
  IPayout,
  IPayoutOverviewTotalsDisplay,
  IPayoutStateByDate,
  IPayoutStateByStatus,
  ISite,
  ISiteWithholdings,
  PayoutApprovalFailedCheckStatus,
  PayoutStatus,
  WithholdingsType,
} from 'types';
import { centsToDollarsDisplay } from './currency';
import { siteUsesWithholdings } from './site';
import { capitalize } from './strings';

/**
 * Generates an alert message based on the payout approval status and failed checks.
 *
 * @param {IPayout} payout - The payout object containing approval status and alert details.
 * @returns {string} The alert message based on the failed check or the default alert message.
 */
export const payoutStatusAlertMessage = (payout: IPayout): string => {
  if (!payout?.alert) return '';

  if (payout.alert.type === AlertType.APPROVAL && payout.approvalCheck) {
    if (!payout.approvalCheck.failedChecks.length) return payout.alert.message;

    const lastFailedCheck = payout.approvalCheck.failedChecks[payout.approvalCheck.failedChecks.length - 1];
    if (lastFailedCheck) {
      switch (lastFailedCheck.name) {
        case PayoutApprovalFailedCheckStatus.LAST_PAYMENT:
          return 'No recent payments';
        case PayoutApprovalFailedCheckStatus.DAILY_VELOCITY_LIMIT:
          return 'Velocity Limit';
        default:
          return payout.alert.message;
      }
    }
  }

  return payout.alert.message;
};

/**
 * Checks if a payout is undelivered, so its disbursement method can still be changed.
 * A payout is undelivered if the current disbursement method is 'instant' and its status is 'pending' or 'expired'.
 *
 * @param {IPayout} payout - The payout object.
 * @returns {boolean} `true` if the payout is undelivered, otherwise `false`.
 */
export const isUndeliveredPayout = (payout: IPayout): boolean => payout?.disbursementMethod === DisbursementMethod.INSTANT && [PayoutStatus.PENDING, PayoutStatus.EXPIRED].includes(payout.status);

/**
 * Takes a tip amount and calculates amount that should be deducted based on the current site holdback settings and payee disbursement method
 *
 * @param {DisbursementMethod} disbursementMethod - Payee disbursement method
 * @param {IHoldbackFixed | IHoldbackPercent} siteHoldbackSettings - Current site holdback settings
 * @param {number} tipAmount - Amount to apply holdback
 * @returns {number} Holdback value
 */
export const calculateHoldbackValue = (disbursementMethod?: DisbursementMethod, siteHoldbackSettings?: IHoldbackFixed | IHoldbackPercent, tipAmount?: number) => {
  if (disbursementMethod !== DisbursementMethod.INSTANT) return 0;

  if (siteHoldbackSettings?.type === HoldbackType.FIXED) return (siteHoldbackSettings as IHoldbackFixed).amount;

  if (!siteHoldbackSettings || !tipAmount || tipAmount < appConfig.TIP.MIN) return 0;

  const percentHoldback = siteHoldbackSettings as IHoldbackPercent;
  const holdbackPercent = percentHoldback.percent / 100; // Get percent as percent.
  const holdbackValue = Math.floor(tipAmount * holdbackPercent);
  if (percentHoldback.cap && holdbackValue > percentHoldback.cap) return percentHoldback.cap; // Return cap when holdback is larger
  return holdbackValue;
};

/**
 * Determines the appropriate withholding configuration for a payee.
 * It returns custom withholdings if employee is configured to use them, otherwise falls back to site-level withholdings.
 *
 * @param {ISite} site - The site object containing withholding settings.
 * @param {IEmployeeSiteWithholdings} [employeeWithholdings] - Optional custom withholding settings for the employee.
 * @returns {ISiteWithholdings | undefined} The applicable withholding configuration.
 */
export const getWithholdingConfig = (site: ISite, employeeWithholdings?: IEmployeeSiteWithholdings): ISiteWithholdings | undefined => {
  if (!siteUsesWithholdings(site)) return undefined;
  if (employeeWithholdings?.type === WithholdingsType.NONE) return undefined;
  return employeeWithholdings?.type === WithholdingsType.CUSTOM ? employeeWithholdings : site.settings.withholdings;
};

/**
 * Calculates the complete payment totals for a payee, including gross, net, cash, withholding, and holdback amounts.
 *
 * @param {number} gross - The gross payment amount.
 * @param {ISite} site - The site object, containing settings for holdback and withholdings.
 * @param {DisbursementMethod} [disbursementMethod] - The employee's disbursement method.
 * @param {IEmployeeSiteWithholdings} [employeeWithholdings] - Specific withholding settings for the employee.
 * @param {number} [currentPayroll] - The employee's current accumulated payroll amount for the period.
 * @returns {IPayeeTotals} An object containing the calculated totals for the payee.
 */
export const calculatePayeeTotals = (gross: number, site: ISite, disbursementMethod?: DisbursementMethod, employeeWithholdings?: IEmployeeSiteWithholdings, currentPayroll?: number): IPayeeTotals => {
  const { holdback: siteHoldback } = site.settings;
  const withholdingConfig = getWithholdingConfig(site, employeeWithholdings);

  const { withholdingAmount } = calculateWithholdings(gross, withholdingConfig, currentPayroll);
  const withheldGross = gross - withholdingAmount;
  let holdbackAmount = calculateHoldbackValue(disbursementMethod, siteHoldback, withheldGross);
  holdbackAmount = holdbackAmount >= withheldGross ? 0 : holdbackAmount; // remove holdback if it covers entire payment after withholdings

  return {
    gross,
    net: gross - holdbackAmount,
    cash: 0,
    withholdingAmount,
    holdbackAmount,
  };
};

/**
 * Calculates the totals for a list of payees.
 *
 * @param {IPayee[]} payees - The list of payees to calculate totals for.
 * @param {ISite} site - The site object, containing settings for holdback and withholdings.
 * @param {IEmployeePayrollTip[]} [payrollTips] - Optional payroll tips data for calculating withholdings based on current payroll totals.
 * @returns {IPayee[]} An array of payees with updated totals.
 */
export const calculatePayeesTotals = (payees: IPayee[], site: ISite, payrollTips?: IEmployeePayrollTip[]): IPayee[] => {
  const payrollMap = new Map(siteUsesWithholdings(site) ? payrollTips?.map((p) => [p.employee.id, p.payroll.total]) : []);

  const payeesWithTotals = payees.map((payee) => {
    const { id, disbursementMethod = DisbursementMethod.INSTANT, withholdings } = payee.employee;
    const totals = calculatePayeeTotals(payee.tipAmount, site, disbursementMethod, withholdings, payrollMap.get(id));
    return {
      ...payee,
      ...totals,
      gross: totals.gross + payee.cash,
      net: totals.net + payee.cash,
      cash: payee.cash,
    };
  });

  return payeesWithTotals;
};

/**
 * Calculates the portion of a payee's earnings to be paid via instant disbursement.
 *
 * @param {IPayee} payee - The payee object.
 * @returns {number} The amount to be paid instantly. Returns 0 if the disbursement method is not 'INSTANT'.
 */
export const calculateInstant = (payee: IPayee) => (payee.employee?.disbursementMethod === DisbursementMethod.INSTANT ? payee.gross - payee.holdbackAmount - payee.withholdingAmount - payee.cash : 0);

/**
 * Calculates the portion of a payee's earnings to be processed through payroll.
 *
 * @param {IPayee} payee - The payee object.
 * @returns {number} The amount to be sent to payroll.
 */
export const calculatePayroll = (payee: IPayee) => (payee.employee?.disbursementMethod === DisbursementMethod.PAYROLL ? payee.gross - payee.cash : payee.withholdingAmount);

/**
 * Calculates withholding for a payment based on its gross amount, a withholding configuration and the amount collected for payroll until now.
 *
 * It determines the withholding amount by considering the greater of a minimum threshold or a percentage rate,
 * while ensuring the total does not exceed a defined cap.
 *
 * @param {number} gross - The gross amount of the payment.
 * @param {ISiteWithholdings} [withholdingConfig] - The site's withholding rules (minimum, rate, cap).
 * @param {number} [currentPayroll=0] - The current payroll total for the employee.
 * @returns {{ withholdingAmount: number }} An object containing the calculated withholding amount.
 */
export const calculateWithholdings = (gross: number, withholdingConfig?: ISiteWithholdings, currentPayroll = 0): { withholdingAmount: number } => {
  let withholdingAmount = 0;

  // If a cap is set and the employee's payroll total already meets or exceeds it, no withholding is needed.
  if (!withholdingConfig || (withholdingConfig.cap > 0 && currentPayroll >= withholdingConfig.cap)) {
    return { withholdingAmount };
  }

  // If the employee's payroll total is less than the minimum withholding amount, calculate the initial amount to withhold.
  if (withholdingConfig.minimum > 0 && currentPayroll < withholdingConfig.minimum) {
    const amountToReachMin = withholdingConfig.minimum - currentPayroll;
    withholdingAmount += Math.min(gross, amountToReachMin);
  }

  // If a withholding rate is configured, calculate and apply the rate-based withholding.
  if (withholdingConfig.rate > 0) {
    const rateAmount = Math.floor((gross * withholdingConfig.rate) / 100);

    if (rateAmount > withholdingAmount) {
      withholdingAmount = rateAmount;

      // If a cap is set, ensure the new payroll total does not exceed it.
      if (withholdingConfig.cap > 0) {
        const newPayrollTotal = currentPayroll + withholdingAmount;
        if (newPayrollTotal > withholdingConfig.cap) {
          withholdingAmount -= newPayrollTotal - withholdingConfig.cap;
        }
      }
    }
  }

  return { withholdingAmount };
};

/**
 * Formats a withholding amount for display.
 *
 * @param {number} withholdingAmount - The amount withheld.
 * @returns {string} A formatted string like "$XX.XX Withheld for taxes and benefits".
 */
export const displayWithholding = (withholdingAmount: number) => `${centsToDollarsDisplay(withholdingAmount)} Withheld for taxes and benefits`;

/**
 * Returns an error message if the total payment exceeds the maximum allowed tip amount.
 *
 * @param totalPaid - The total amount paid.
 * @param maxTipAmount - The maximum allowed tip amount for the site.
 * @returns The error message string if the limit is exceeded, otherwise false.
 */
export const getPaymentExceedsLimitError = (totalPaid: number, maxTipAmount?: number) =>
  maxTipAmount !== undefined && totalPaid > maxTipAmount && `Payment exceeds this site's maximum payment amount of ${centsToDollarsDisplay(maxTipAmount)}`;

/**
 * Determines which payout types are included in the current set of payees.
 *
 * @param {IPayee[]} payees - The list of payees to check.
 * @param {boolean} siteHoldbacks - Indicates if the site has holdbacks enabled.
 * @returns {IPayoutOverviewTotalsDisplay} An object indicating the presence of each payout type.
 */
export const getIncludedPayoutTypes = (payees: IPayee[], siteHoldbacks: boolean): IPayoutOverviewTotalsDisplay => {
  let hasCash = false;
  let hasInstant = false;
  let hasPayroll = false;

  payees.some((payee) => {
    if (payee.cash > 0) hasCash = true;
    if (payee.employee?.disbursementMethod === DisbursementMethod.INSTANT) hasInstant = true;
    if (payee.employee?.disbursementMethod === DisbursementMethod.PAYROLL || payee.withholdingAmount > 0) hasPayroll = true;
    return hasCash && hasInstant && hasPayroll;
  });

  return {
    cash: hasCash,
    holdbacks: hasInstant && siteHoldbacks,
    instant: hasInstant,
    payroll: hasPayroll,
  };
};

/**
 * Counts the number of included payouts by filtering out falsy values from the IPayoutOverviewTotalsDisplay
 *
 * @param {IPayoutOverviewTotalsDisplay} includedPayouts - Object containing boolean values indicating inclusion status
 * @returns {number} The count of included payouts (truthy values)
 */
export const getIncludedPayoutsCount = (includedPayouts: IPayoutOverviewTotalsDisplay) => Object.values(includedPayouts).filter(Boolean).length;

const groupOrderByDisbursementMethod = { instant: 0, payroll: 1, cash: 2 };

/**
 * Sorts an array of payouts by disbursement method in the order: instant, payroll, cash.
 *
 * @param {IPayout[]} payouts - The array of payouts to sort.
 * @returns {IPayout[]} The sorted array of payouts.
 */
export const sortPayoutsByDisbursementMethod = (payouts: IPayout[]) => payouts.sort((a, b) => (groupOrderByDisbursementMethod[a.disbursementMethod] || 0) - (groupOrderByDisbursementMethod[b.disbursementMethod] || 0));

/**
 * Generates display properties for a payout's status.
 *
 * @param {IPayout} [payout] - The payout object to evaluate.
 * @returns {{ statusLabel?: DisbursementMethod; subStatusLabel?: string; subStatusAppearance?: Appearance } | null} An object containing status display properties.
 */
export const getPayoutStatusDisplayProps = (payout?: IPayout): { statusLabel?: DisbursementMethod; subStatusLabel?: string; subStatusAppearance?: Appearance } | null => {
  const includeSubStatus = [PayoutStatus.PENDING, PayoutStatus.EXPIRED, PayoutStatus.CANCELED].includes(payout?.status);
  return {
    statusLabel: payout?.disbursementMethod,
    subStatusLabel: includeSubStatus ? `${capitalize(payout.status)}${payout.alert?.label ? ` - ${payout.alert.label}` : ''}` : undefined,
    subStatusAppearance: getStatusColor(payout.status),
  };
};

/**
 * Counts the number of payouts for a given status.
 *
 * @param {IPayoutStateByStatus[]} statsByStatus - The array of payout statuses.
 * @param {PayoutStatus} status - The status to count.
 * @returns {number} The count of payouts by status.
 */
export const getPayoutStatusCount = (statsByStatus: IPayoutStateByStatus[], status: PayoutStatus): number => {
  const byStatus = (statsByStatus || []).filter((stat) => stat.status === status);
  return sumBy(byStatus, 'count');
};

/**
 * Calculates the total net amount of payouts for a given status.
 *
 * @param {IPayoutStateByStatus[]} statsByStatus - The array of payout statuses.
 * @param {PayoutStatus} status - The status to calculate the total net amount for.
 * @returns {number} The total net amount of payouts by status.
 */
export const getPayoutNetTotalByStatus = (statsByStatus: IPayoutStateByStatus[], status: PayoutStatus): number => {
  const byStatus = (statsByStatus || []).filter((stat) => stat.status === status);
  return sumBy(byStatus, 'net');
};

/**
 * Returns payout stats by date with canceled payouts excluded from each date's totals.
 * For each date that has canceled payouts in byStatus, subtract the canceled amounts from
 * autoGratuity, cash, instant, payroll, net, gross, holdbacks, and count.
 *
 * @param {IPayoutStateByDate[]} payoutStatsByDate - Per-date payout stats that may include byStatus breakdown.
 * @returns {IPayoutStateByDate[]} Same structure with canceled amounts subtracted from each date's totals.
 */
export const statsByDateWithoutCanceled = (payoutStatsByDate: IPayoutStateByDate[]): IPayoutStateByDate[] =>
  payoutStatsByDate.map((stat) => {
    const canceled = stat.byStatus?.find((s) => s.status === PayoutStatus.CANCELED);
    if (!canceled) return stat;
    return {
      ...stat,
      autoGratuity: stat.autoGratuity - (canceled.autoGratuity || 0),
      cash: stat.cash - (canceled.cash || 0),
      instant: stat.instant - (canceled.instant || 0),
      payroll: stat.payroll - (canceled.payroll || 0),
      net: stat.net - (canceled.net || 0),
      gross: stat.gross - (canceled.gross || 0),
      holdbacks: stat.holdbacks - (canceled.holdbacks || 0),
      count: stat.count - (canceled.count || 0),
    };
  });

/**
 * Sums numeric fields across all per-date payout stats into a single totals object.
 *
 * @param {IPayoutStateByDate[]} payoutStatsByDate - Per-date payout stats to sum.
 * @returns {IBasePayoutState | undefined} Aggregated totals, or undefined when input is null/undefined; zeros when empty array.
 */
export const calculatePayoutStatTotals = (payoutStatsByDate: IPayoutStateByDate[]): IBasePayoutState =>
  (payoutStatsByDate || []).reduce<IBasePayoutState>(
    (acc, stat) => {
      acc.net += stat.net;
      acc.gross += stat.gross;
      acc.holdbacks += stat.holdbacks;
      acc.count += stat.count;
      acc.instant += stat.instant;
      acc.payroll += stat.payroll;
      acc.autoGratuity += stat.autoGratuity;
      acc.cash += stat.cash;
      return acc;
    },
    { net: 0, gross: 0, holdbacks: 0, count: 0, instant: 0, payroll: 0, autoGratuity: 0, cash: 0 }
  );
